import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowRight, ChevronDown, CornerDownRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AUDIT_SUMMARY_FIELDS,
  formatAuditDate,
  formatChangeValue,
  getVisibleActorFields,
} from "@/services/auditTrail";

const labelClass =
  "text-[11px] font-medium uppercase tracking-wide text-[#8c8c8c]";
const valueClass = "text-sm font-semibold text-[#3c3c3c]";

function summaryValue(field, entry) {
  const raw = entry[field.key];
  if (raw == null || String(raw).trim() === "") return "—";
  return field.isDate ? formatAuditDate(raw) : String(raw);
}

function ActorLine({ label, value }) {
  return (
    <div>
      <div className={labelClass}>{label}</div>
      <div className={cn(valueClass, "break-words")}>
        {value == null || String(value).trim() === "" ? "—" : String(value)}
      </div>
    </div>
  );
}

ActorLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function InformationChange({ changes }) {
  if (!changes?.length) return null;

  return (
    <div className="mt-4" data-testid="view-paf-audit-trail-changes">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#3c3c3c]">
        <CornerDownRight className="h-4 w-4 text-[#8c8c8c]" aria-hidden />
        Information Change
      </div>
      <div className="mt-2 space-y-3 border-l-2 border-[#e0e0e0] pl-4">
        {changes.map((change, index) => (
          <div key={`${change.actor}-${change.timestamp}-${index}`}>
            {(change.actor || change.timestamp) && (
              <div className="mb-1 text-xs text-[#8c8c8c]">
                {[change.actor, formatAuditDate(change.timestamp, "dd-MMM-yyyy, HH:mm")]
                  .filter((part) => part && part !== "—")
                  .join(" • ")}
              </div>
            )}
            <ul className="space-y-1">
              {change.rows.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-wrap items-center gap-1.5 text-sm text-[#3c3c3c]"
                >
                  <span className="font-semibold">{row.label}</span>
                  <span className="text-[#8c8c8c]">
                    {formatChangeValue(row.from)}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-[#8c8c8c]" aria-hidden />
                  <span className="font-semibold">{formatChangeValue(row.to)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

InformationChange.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      actor: PropTypes.string,
      timestamp: PropTypes.string,
      rows: PropTypes.array,
    })
  ),
};

const PrescriptionAuditTrailCard = ({ entry, className, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!entry) return null;

  const summaryFields = AUDIT_SUMMARY_FIELDS.filter(
    (field) => entry[field.key] != null && String(entry[field.key]).trim() !== ""
  );
  const actorFields = getVisibleActorFields(entry);
  const contentId = `audit-card-${entry.prescription_id ?? "entry"}`;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-[#e0e0e0] bg-white",
        className
      )}
      data-testid="view-paf-audit-trail-card"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        data-testid="view-paf-audit-trail-toggle"
        className={cn(
          "flex w-full items-start gap-3 bg-[#f5f7f8] px-5 py-4 text-left transition-colors hover:bg-[#eef2f4]",
          open && "border-b border-[#e0e0e0]"
        )}
      >
        <div
          className="grid flex-1 grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 lg:grid-cols-4"
          data-testid="view-paf-audit-trail-summary"
        >
          {summaryFields.map((field) => (
            <div key={field.key}>
              <div className={labelClass}>{field.label}</div>
              <div className={valueClass}>{summaryValue(field, entry)}</div>
            </div>
          ))}
        </div>
        <ChevronDown
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0 text-[#8c8c8c] transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {open && (
        <div
          id={contentId}
          className="px-5 py-4"
          data-testid="view-paf-audit-trail-details"
        >
          <div className="space-y-3" data-testid="view-paf-audit-trail-actors">
            {actorFields.map((field) => (
              <ActorLine key={field.key} label={field.label} value={field.value} />
            ))}
          </div>

          <InformationChange changes={entry.information_changed} />
        </div>
      )}
    </div>
  );
};

PrescriptionAuditTrailCard.propTypes = {
  entry: PropTypes.shape({
    prescription_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    information_changed: PropTypes.array,
  }),
  className: PropTypes.string,
  defaultOpen: PropTypes.bool,
};

export default PrescriptionAuditTrailCard;
